import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './TechnicalService.scss';

const getTokenPayload = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    return null;
  }
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }
  if (typeof data?.Message === 'string' && data.Message.trim()) {
    return data.Message;
  }
  if (Array.isArray(data?.messages) && data.messages.length > 0) {
    return data.messages.join(', ');
  }
  if (Array.isArray(data?.Messages) && data.Messages.length > 0) {
    return data.Messages.join(', ');
  }
  if (data?.errors && typeof data.errors === 'object') {
    const errorValues = Object.values(data.errors).flat();
    if (errorValues.length > 0) {
      return errorValues.join(', ');
    }
  }
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (error?.response?.status) {
    return `${fallback} (HTTP ${error.response.status})`;
  }
  if (error?.message) {
    return `${fallback} (${error.message})`;
  }
  return fallback;
};

const isGuid = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const TechnicalServiceCreateRequestPage = () => {
  const payload = getTokenPayload();
  const companyId = payload?.CompanyId || '';
  const currentUserId =
    payload?.userId ||
    payload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
    '';
  const userRole =
    payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '';

  const isTechnicalManager = userRole === 'TechnicalServiceManager' || userRole === 'Owner';
  const [towerCranes, setTowerCranes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState({
    categoryType: '1',
    requestingCompanyId: companyId || '',
    towerCraneId: '',
    externalCompanyName: '',
    externalMachineName: '',
    externalMachineModel: '',
    externalWorkSiteName: '',
    workSiteLocation: '',
    operationDate: '',
    subject: '',
    serviceType: '0',
    problemDescription: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [craneRes, rentalRes] = await Promise.all([
          api.get('/TowerCrane'),
          api.get('/Rental', { params: { serviceStatus: 'Approved', includeLineItems: false } }).catch(() => ({ data: { data: [] } }))
        ]);

        setTowerCranes(craneRes?.data?.data || []);
        setRentals(rentalRes?.data?.data || []);
      } catch (error) {
        console.error('Teknik servis talep ekranı verileri alınamadı:', error);
        setErrorMessage('Teknik servis talep ekranı yüklenemedi.');
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'towerCraneId' ? { externalWorkSiteName: '', workSiteLocation: '' } : {})
    }));
  };

  const canOpenRequest =
    userRole === 'Company' || userRole === 'TechnicalServiceManager' || userRole === 'Owner';

  const managerUserId = isGuid(currentUserId) ? currentUserId : null;

  const availableTowerCranes = useMemo(() => {
    return towerCranes;
  }, [towerCranes]);

  const activeRentalByCraneId = useMemo(() => {
    const lookup = new Map();
    rentals
      .filter((rental) => rental?.isActive && (rental?.serviceStatus === 2 || rental?.serviceStatus === 'Approved'))
      .forEach((rental) => {
        lookup.set(Number(rental.towerCraneId), rental);
      });
    return lookup;
  }, [rentals]);

  const selectedActiveRental = useMemo(
    () => activeRentalByCraneId.get(Number(form.towerCraneId)) || null,
    [activeRentalByCraneId, form.towerCraneId]
  );

  const selectedTowerCrane = useMemo(
    () => towerCranes.find((crane) => String(crane.id) === String(form.towerCraneId)) || null,
    [towerCranes, form.towerCraneId]
  );

  const fallbackRequestingCompanyId = form.requestingCompanyId || companyId || selectedTowerCrane?.ownerId || '';
  const effectiveCompanyId =
    form.categoryType === '1'
      ? selectedActiveRental?.rentingCompanyId || fallbackRequestingCompanyId
      : companyId || form.requestingCompanyId;
  const selectedRequestingCompanyId = selectedActiveRental?.rentingCompanyId || fallbackRequestingCompanyId;
  const selectedWorkSiteId = selectedActiveRental?.workSiteId || null;
  const selectedWorkSiteName = form.externalWorkSiteName || selectedActiveRental?.workSite;

  const requestTypeLabel = useMemo(
    () => (form.categoryType === '1' ? 'Kayıtlı Vinç Talebi' : 'Dış Teknik Servis Talebi'),
    [form.categoryType]
  );

  useEffect(() => {
    if (!selectedActiveRental) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      externalWorkSiteName: prev.externalWorkSiteName || selectedActiveRental.workSite || '',
      workSiteLocation: prev.workSiteLocation || selectedActiveRental.workSite || ''
    }));
  }, [selectedActiveRental]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!effectiveCompanyId) {
      setErrorMessage('Talep açan firma seçilmelidir.');
      setSubmitting(false);
      return;
    }

    if (form.categoryType === '1' && !form.towerCraneId) {
      setErrorMessage('Kayıtlı vinç talebinde vinç seçilmelidir.');
      setSubmitting(false);
      return;
    }

    if (form.categoryType === '1' && selectedTowerCrane?.hasOpenTechnicalService) {
      setErrorMessage('Bu vinç için zaten açık bir teknik servis süreci var.');
      setSubmitting(false);
      return;
    }

    if (form.categoryType === '2' && !form.externalCompanyName.trim()) {
      setErrorMessage('Dış teknik servis için firma adı manuel girilmelidir.');
      setSubmitting(false);
      return;
    }

    if (!form.workSiteLocation.trim()) {
      setErrorMessage('Konum girilmelidir.');
      setSubmitting(false);
      return;
    }

    if (!form.operationDate) {
      setErrorMessage('Operasyon tarihi girilmelidir.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        requestingCompanyId:
          form.categoryType === '1' ? selectedRequestingCompanyId : effectiveCompanyId,
        categoryType: Number(form.categoryType),
        towerCraneId:
          form.categoryType === '1' && form.towerCraneId
            ? Number(form.towerCraneId)
            : null,
        workSiteId: form.categoryType === '1' ? selectedWorkSiteId : null,
        externalCompanyName:
          form.categoryType === '2' ? form.externalCompanyName : null,
        externalMachineName:
          form.categoryType === '2' ? form.externalMachineName : null,
        externalMachineModel:
          form.categoryType === '2' ? form.externalMachineModel : null,
        externalSerialNo: null,
        externalWorkSiteName:
          form.categoryType === '1' ? selectedWorkSiteName || null : form.externalWorkSiteName || null,
        workSiteLocation: form.workSiteLocation || null,
        operationDate: form.operationDate || null,
        subject: form.subject,
        serviceType: Number(form.serviceType),
        problemDescription: form.problemDescription,
        assignedManagerUserId: isTechnicalManager ? managerUserId : null
      };

      console.log('Technical service request payload:', payload);
      await api.post('/TechnicalService/requests', payload);

      setSuccessMessage('Teknik servis talebi oluşturuldu. Talep müdür ekranına düştü.');
      if (payload.towerCraneId) {
        setTowerCranes((prev) =>
          prev.map((crane) =>
            Number(crane.id) === Number(payload.towerCraneId)
              ? { ...crane, hasOpenTechnicalService: true }
              : crane
          )
        );
      }
      setForm({
        categoryType: '1',
        requestingCompanyId: companyId || '',
        towerCraneId: '',
        externalCompanyName: '',
        externalMachineName: '',
        externalMachineModel: '',
        externalWorkSiteName: '',
        workSiteLocation: '',
        operationDate: '',
        subject: '',
        serviceType: '0',
        problemDescription: ''
      });
    } catch (error) {
      console.error('Teknik servis talebi oluşturulamadı:', error);
      console.error('Technical service error response:', error?.response);
      setErrorMessage(getApiErrorMessage(error, 'Talep oluşturulamadı.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canOpenRequest) {
    return (
      <div className="technical-service-page">
        <Navbar />
        <div className="technical-service-container">
          <div className="technical-service-card">
            <h2>Bu alan yalnızca firma kullanıcıları ve teknik servis müdürü için görünür.</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="technical-service-page technical-service-create-page">
      <Navbar />
      <div className="technical-service-container">
        <div className="technical-service-header">
          <div>
            <h1>Teknik Servis Talep Oluştur</h1>
          </div>
        </div>

        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}
        {successMessage && <div className="technical-service-alert success">{successMessage}</div>}

        <section className="technical-service-card request-create-card">
          <h2>{requestTypeLabel}</h2>
          <form className="technical-service-form request-create-form" onSubmit={handleSubmit}>
            <div className="field-grid request-create-grid">

              <label>
                Talep Tipi
                <select
                  name="categoryType"
                  value={form.categoryType}
                  onChange={handleInputChange}
                >
                  <option value="1">Kayıtlı Vinç</option>
                  <option value="2">Dış Teknik Servis</option>
                </select>
              </label>

              <label>
                Şantiye
                <input
                  name="externalWorkSiteName"
                  value={form.externalWorkSiteName}
                  onChange={handleInputChange}
                  placeholder="Şantiye adını yazın"
                />
              </label>

              {form.categoryType === '1' ? (
                <label className="full-width">
                  Kayıtlı Vinçler
                  <select
                    name="towerCraneId"
                    value={form.towerCraneId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Vinç seçin</option>
                    {availableTowerCranes.map((crane) => (
                      <option key={crane.id} value={crane.id} disabled={crane.hasOpenTechnicalService}>
                        {crane.serialNo} - {crane.model}
                        {crane.hasOpenTechnicalService ? ' - Teknik serviste' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <label>
                    Dış Firma
                    <input
                      name="externalCompanyName"
                      value={form.externalCompanyName}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Makine Adı
                    <input
                      name="externalMachineName"
                      value={form.externalMachineName}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Model
                    <input
                      name="externalMachineModel"
                      value={form.externalMachineModel}
                      onChange={handleInputChange}
                    />
                  </label>
                </>
              )}

              {form.categoryType === '1' && selectedActiveRental && (
                <div className="full-width rental-context-box">
                  <div><strong>Kiracı Firma:</strong> {selectedActiveRental.rentingCompany || '-'}</div>
                  <div><strong>Şantiye:</strong> {selectedActiveRental.workSite || '-'}</div>
                </div>
              )}

              <label>
                Konum
                <input
                  name="workSiteLocation"
                  value={form.workSiteLocation}
                  onChange={handleInputChange}
                  placeholder="Örn: Kocaeli / Dilovası"
                  required
                />
              </label>

              <label>
                Operasyon Tarihi
                <input
                  type="datetime-local"
                  name="operationDate"
                  value={form.operationDate}
                  onChange={handleInputChange}
                  required
                />
              </label>

              <label>
                Servis Konusu
                <select
                  name="serviceType"
                  value={form.serviceType}
                  onChange={handleInputChange}
                >
                  <option value="1">Bakım</option>
                  <option value="2">Parça Değişimi</option>
                  <option value="3">Arıza Giderme</option>
                  <option value="4">Montaj</option>
                  <option value="5">Söküm</option>
                  <option value="6">Kontrol</option>
                  <option value="7">Diğer</option>
                </select>
              </label>

              <label className="full-width">
                Başlık
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleInputChange}
                  required
                />
              </label>

              <label className="full-width">
                Problem Açıklaması
                <textarea
                  name="problemDescription"
                  value={form.problemDescription}
                  onChange={handleInputChange}
                  rows="4"
                />
              </label>
            </div>

            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Talep Oluştur'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default TechnicalServiceCreateRequestPage;

